import { motion } from 'framer-motion'
import { Receipt, Download, CreditCard } from 'lucide-react'
import { PageHeader, useAuthStore, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { useBillingSummary } from '@/hooks/useBilling'

const mockInvoices = [
  { id: 'INV-2026-003', date: '2026-02-01', amount: '$299.00', status: 'paid' },
  { id: 'INV-2026-002', date: '2026-01-01', amount: '$299.00', status: 'paid' },
  { id: 'INV-2026-001', date: '2025-12-01', amount: '$299.00', status: 'paid' },
]

export default function Billing() {
  const { user } = useAuthStore()
  const tenantId = user?.tenant_id || ''
  const { data: summary } = useBillingSummary(tenantId)

  const breadcrumbs = buildBreadcrumbs('/settings/account/billing')
  const isTrial = summary?.plan === 'trial'

  return (
    <div>
      <PageHeader
        title="Billing & Invoices"
        description="View payment history, manage payment methods, and download invoices."
        breadcrumbs={breadcrumbs}
      />

      <div className="space-y-6 max-w-4xl">
        {isTrial && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" className="rounded-xl border border-copper-200 bg-copper-50 p-4">
            <p className="text-sm text-copper-700">
              You're on a trial plan. No charges have been made yet. Upgrade to start your subscription.
            </p>
          </motion.div>
        )}

        {/* Payment Method */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="rounded-xl border border-warm-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-copper-500" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-midnight-800">Payment Method</h2>
              <p className="text-xs text-warm-500">Manage your billing details</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-warm-50 border border-warm-200">
            <div className="w-10 h-7 rounded bg-midnight-800 flex items-center justify-center">
              <span className="text-white text-[10px] font-bold">VISA</span>
            </div>
            <div>
              <p className="text-sm font-medium text-midnight-800">Visa ending in 4242</p>
              <p className="text-xs text-warm-500">Expires 12/2027</p>
            </div>
          </div>
        </motion.div>

        {/* Invoice History */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="rounded-xl border border-warm-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-copper-500" />
            </div>
            <h2 className="font-heading font-semibold text-midnight-800">Invoice History</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-warm-200">
                  <th className="text-left py-2 text-xs text-warm-500 uppercase tracking-wide font-medium">Invoice</th>
                  <th className="text-left py-2 text-xs text-warm-500 uppercase tracking-wide font-medium">Date</th>
                  <th className="text-left py-2 text-xs text-warm-500 uppercase tracking-wide font-medium">Amount</th>
                  <th className="text-left py-2 text-xs text-warm-500 uppercase tracking-wide font-medium">Status</th>
                  <th className="text-right py-2 text-xs text-warm-500 uppercase tracking-wide font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {mockInvoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-warm-100">
                    <td className="py-3 font-medium text-midnight-800">{inv.id}</td>
                    <td className="py-3 text-warm-600">{inv.date}</td>
                    <td className="py-3 text-midnight-800">{inv.amount}</td>
                    <td className="py-3">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-sage-50 text-sage-600">
                        {inv.status}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <button className="text-copper-600 hover:text-copper-700 inline-flex items-center gap-1 text-xs">
                        <Download className="w-3 h-3" /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
